import FeatureAction from './FeatureAction';
import { useFeatureAccess } from '../contexts/FeatureAccessContext';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTabActivity } from '../contexts/TabActivityContext';
import { MessageSquare, X } from 'lucide-react';
import {
  apiService,
  MarkerColor,
  MarkerEntityType,
  MarkerInfo,
  MarkerRecord,
  UpsertMarkerRequest,
} from '../services/api';
import './MarkerEditor.css';

const COLORS: MarkerColor[] = ['red', 'orange', 'yellow', 'green', 'blue', 'gray'];

const emptyMarkerInfo = (): MarkerInfo => ({
  marker_id: null,
  color: null,
  note: null,
  updated_at: null,
});

const markerInfoFromRecord = (record: MarkerRecord | null | undefined): MarkerInfo => ({
  marker_id: record?.marker_id ?? null,
  color: record?.color ?? null,
  note: record?.note ?? null,
  updated_at: record?.updated_at ?? null,
});

export interface MarkerTarget extends Omit<UpsertMarkerRequest, 'color' | 'note'> {
  entity_type: MarkerEntityType;
}

interface MarkerEditorProps {
  marker?: MarkerInfo;
  target: MarkerTarget;
  className?: string;
  disabled?: boolean;
  onMarkerChange?: (marker: MarkerInfo) => void;
  onRecordChange?: (record: MarkerRecord | null) => void;
}

const MarkerEditor: React.FC<MarkerEditorProps> = ({
  marker,
  target,
  className,
  disabled = false,
  onMarkerChange,
  onRecordChange,
}) => {
  const { isActive, portalContainer } = useTabActivity();
  const { hasFeature } = useFeatureAccess();
  const canEdit = hasFeature('workspace_mutation');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties | undefined>(undefined);

  const initialColor = marker?.color ?? null;
  const initialNote = marker?.note ?? '';

  const [color, setColor] = useState<MarkerColor | null>(initialColor);
  const [note, setNote] = useState<string>(initialNote);

  const hasNote = Boolean((marker?.note || '').trim());
  const triggerColor = marker?.color ?? null;

  const triggerLabel = useMemo(() => {
    if (triggerColor || hasNote) return 'Edit marker';
    return 'Add marker';
  }, [triggerColor, hasNote]);

  useEffect(() => {
    setColor(initialColor);
    setNote(initialNote);
  }, [initialColor, initialNote]);

  useEffect(() => {
    if (!open || !isActive) return;

    const handlePointerDown = (event: MouseEvent) => {
      const el = wrapperRef.current;
      const pop = popoverRef.current;
      if (!el) return;
      const targetNode = event.target as Node;
      if (!el.contains(targetNode) && !(pop && pop.contains(targetNode))) {
        setOpen(false);
        setError(null);
        setColor(initialColor);
        setNote(initialNote);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, isActive, initialColor, initialNote]);

  useEffect(() => {
    if (!open || !isActive) return;

    const popoverWidth = 260;
    const margin = 8;

    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.getBoundingClientRect().height || 0;

      // Default placement: below trigger, right-aligned.
      let left = rect.right - popoverWidth;
      left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin));

      let top = rect.bottom + 8;
      if (popoverHeight && top + popoverHeight > window.innerHeight - margin) {
        const above = rect.top - 8 - popoverHeight;
        if (above >= margin) {
          top = above;
        } else {
          top = Math.max(margin, window.innerHeight - popoverHeight - margin);
        }
      }

      setPopoverStyle({
        top: Math.round(top),
        left: Math.round(left),
        width: popoverWidth,
      });
    };

    // Compute immediately and again after layout settles (fonts/scrollbars).
    update();
    const raf1 = requestAnimationFrame(update);
    const raf2 = requestAnimationFrame(update);

    window.addEventListener('resize', update);
    // Capture scroll events from any scroll container.
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, isActive]);

  const emit = (record: MarkerRecord | null) => {
    const info = record ? markerInfoFromRecord(record) : emptyMarkerInfo();
    onRecordChange?.(record);
    onMarkerChange?.(info);
  };

  const submit = async (nextColor: MarkerColor | null, nextNoteRaw: string | null, closeOnSuccess: boolean) => {
    if (!canEdit) return;
    if (disabled || saving) return;
    setSaving(true);
    setError(null);
    try {
      const normalizedNote = (() => {
        if (nextNoteRaw === null) return null;
        const trimmed = `${nextNoteRaw}`.trim();
        return trimmed.length ? trimmed : null;
      })();

      const payload: UpsertMarkerRequest = {
        ...target,
        color: nextColor,
        note: normalizedNote,
      };

      const record = await apiService.upsertMarker(payload);
      emit(record);

      if (closeOnSuccess) {
        setOpen(false);
      }
    } catch (err: any) {
      console.error('Failed to save marker:', err);
      setError(err?.response?.data?.detail?.message || 'Failed to save marker');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => submit(color, note, true);

  const handleClear = async () => submit(null, null, true);

  const popover = open && canEdit ? (
    <div
      ref={popoverRef}
      className="marker-popover"
      style={popoverStyle}
      role="dialog"
      aria-label="Marker editor"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="marker-popover-header">
        <div className="marker-popover-title">Marker</div>
        <button
          type="button"
          className="marker-close"
          aria-label="Close"
          onClick={() => {
            setOpen(false);
            setError(null);
            setColor(initialColor);
            setNote(initialNote);
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div className="marker-section">
        <div className="marker-section-label">Color</div>
        <div className="marker-colors">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`marker-color ${c} ${color === c ? 'selected' : ''}`}
              aria-label={`Set color ${c}`}
              title={c}
              disabled={saving}
              onClick={() => setColor((prev) => (prev === c ? null : c))}
            />
          ))}
        </div>
      </div>

      <div className="marker-section">
        <div className="marker-section-label">Note</div>
        <textarea
          className="marker-note"
          value={note}
          placeholder="Add a note..."
          disabled={saving}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
        />
      </div>

      {error && <div className="marker-error">{error}</div>}

      <div className="marker-actions">
        <button type="button" className="marker-btn secondary" disabled={saving} onClick={handleClear}>
          Clear
        </button>
        <button
          type="button"
          className="marker-btn secondary"
          disabled={saving}
          onClick={() => {
            setOpen(false);
            setError(null);
            setColor(initialColor);
            setNote(initialNote);
          }}
        >
          Cancel
        </button>
        <button type="button" className="marker-btn primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={wrapperRef} className={`marker-editor ${className || ''}`}>
      <FeatureAction feature="workspace_mutation"><button
        ref={triggerRef}
        type="button"
        className={`marker-trigger ${triggerColor ? `is-${triggerColor}` : 'is-empty'} ${hasNote ? 'has-note' : ''}`}
        aria-label={triggerLabel}
        title={triggerLabel}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
          setError(null);
        }}
      >
        <span className="marker-dot" aria-hidden="true" />
        {hasNote && <MessageSquare size={12} className="marker-note-icon" aria-hidden="true" />}
      </button></FeatureAction>

      {open && createPortal(popover, portalContainer || document.body)}
    </div>
  );
};

export default MarkerEditor;
